import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ProductUpdateRequest } from '@/api/handlers/resources/products/requests';
import type { GetProductDetailResponse } from '@/api/handlers/resources/products/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { ProductQueryKeys } from './productQueryKeys';

export type UpdateProductVariables = {
    id: string;
    body: ProductUpdateRequest;
};

/**
 * Fields-only update behind the edit form and a draft's Publish. On success it
 * first writes the reply's `draft` and `draftIssues` into every cached detail
 * of the product (under every branch, nothing else touched, as the reply
 * counts stock for the caller's scope), so a published draft's panel goes at
 * once: it reads the flag from the query, not from a copy (review of
 * bos-app#229, finding 19). Then it invalidates the product's detail cache and
 * every list cache so the edited values show up, and every Barcode issues page, as
 * the form can change the main barcode (review of #241, finding 8). The query function never
 * rejects — the form branches on `result.success` and surfaces the server's
 * 400 `error` text inline.
 */
export const useUpdateProductMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetProductDetailResponse>,
        Error,
        UpdateProductVariables
    >({
        mutationFn: ({ id, body }) =>
            apiClient.products.updateProduct(id, body),
        onSuccess: (result, variables) => {
            if (result.success) {
                const { draft, draftIssues } = result.response;
                queryClient.setQueriesData<
                    ApiResponse<GetProductDetailResponse>
                >(
                    { queryKey: ProductQueryKeys.detail(variables.id) },
                    (cached) =>
                        cached?.success
                            ? {
                                  ...cached,
                                  response: {
                                      ...cached.response,
                                      draft,
                                      draftIssues,
                                  },
                              }
                            : cached,
                );
                queryClient.invalidateQueries({
                    queryKey: ProductQueryKeys.detail(variables.id),
                });
                queryClient.invalidateQueries({
                    queryKey: ProductQueryKeys.lists(),
                });
                queryClient.invalidateQueries({
                    queryKey: ProductQueryKeys.barcodeIssueLists(),
                });
            }
        },
    });
};
